import { Injectable } from '@angular/core';
import { DiffConstants } from '@app/core/constants/diff-constants';
import { LOCAL_IMAGES } from '@app/core/constants/img-constants';
import { STORAGE_CONST } from '@app/core/constants/storage-constants';
import { CostPerPage } from '@app/shared/models/DomainCustomizationText';
import { EXTENSION_TO_MIME } from '@app/core/constants/string-constants';
import { TwoOptionsPopupComponent } from '@app/core/modals/two-options-popup/two-options-popup.component';
import { DomainInfo } from '@app/shared/models/DomainInfo';
import { MsalService } from '@azure/msal-angular';
import { CountpagesService } from '@core/services/countpages/countpages.service';
import { StorageService } from '@core/services/storage/storage.service';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LocalFile } from '@shared/models/file-types';
import { BehaviorSubject } from 'rxjs';

interface MinDomainInfo {
  domainname: string;
  location: string;
  device_id: string;
  locationId: string;
  customerId: string;
  locationName?: string;
}
@Injectable({
  providedIn: 'root',
})
export class SharedService {
  public getCustomerDetails: DomainInfo;
  public tempCustomerDetails: DomainInfo;
  public tempDomainInfo: MinDomainInfo;
  public fileData: any;
  public item: any;
  public totalSize = 0;
  public totalSizeToPreview: string;
  public jobCostToShow = '0.0';
  public uploadedFiles: LocalFile[] = [];
  public dataArrayUploadFile = [];
  public commingFromIntent: boolean = false;
  public refreshAccountsPage: boolean = false;
  public accountOnly: boolean = false;
  public showAccountOnlySection = true;
  public domaininfo: MinDomainInfo;
  checkStorageAccountStatus: BehaviorSubject<boolean> = new BehaviorSubject(undefined);
  public drawerHeaderLogo = LOCAL_IMAGES.eprintitLogo;
  public selectedDomainThroughURL = false;
  currencySymbol = '$';
  isNavigateFromUpload: boolean = false;

  constructor(
    private storageSvc: StorageService,
    private countpagesSvc: CountpagesService,
    private modalCtrl: ModalController,
    private translateService: TranslateService,
    private authService: MsalService,
    private platform: Platform
  ) {}

  isDomainReady(): boolean {
    return this.domaininfo !== undefined;
  }

  setDomainInfo(data: DomainInfo) {
    this.getCustomerDetails = data;
    this.setCurrencySymbol();
    this.storageSvc.setJson(STORAGE_CONST.CUSTOMER_INFO, data);
  }
  async getCustomerInfo() {
    return await this.storageSvc.getJson(STORAGE_CONST.CUSTOMER_INFO);
  }
  async getDomainInfo() {
    return await this.storageSvc.getJson(STORAGE_CONST.DOMAIN_INFO);
  }

  addBlobToQueue(file: LocalFile, dataB64: any): Promise<number> {
    // const data = this.base64ArrayBuffer(blob);
    return new Promise<number>((resolve, reject) => {
      const idx = new Date().getTime();
      this.countpagesSvc.getPageCount(dataB64, file.fileName, idx).subscribe({
        next: (result) => {
          // this.getPageCountSuccess(res, prefs, file)
          this.addFile(result, file);
          resolve(1);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  addFileToQueueAndroid(file): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const localFile: LocalFile = {
        content: file.data,
        contentType: file.type || file.mimeType,
        extension: '',
        fileName: file.name,
        lastModified: file.lastModified,
        size: file.size,
      };

      const idx = new Date().getTime();
      this.countpagesSvc.getPageCount(file.data, localFile.fileName, idx).subscribe({
        next: (result) => {
          // this.getPageCountSuccess(res, prefs, file)
          this.addFile(result, localFile);
          const size = Number(localFile.size * 0.000001);
          this.totalSize += size;
          this.totalSizeToPreview = this.totalSize.toFixed(2);
          resolve(1);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  addFileToQueue(file): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      // Pass the native File straight through instead of pre-reading it into an
      // ArrayBuffer: File/Blob support cheap, lazy `.slice()` reads, so the whole
      // file never has to sit in main-thread memory just to count pages (needed
      // to support docx/xlsx/pptx/odt uploads in the hundreds of MB). upload.service.ts
      // already handles `content instanceof Blob` directly.
      const localFile: LocalFile = {
        content: file,
        contentType: file.type,
        extension: '',
        fileName: file.name,
        lastModified: file.lastModified,
        size: file.size,
      };

      const idx = new Date().getTime();
      this.countpagesSvc.getPageCount(file, localFile.fileName, idx).subscribe({
        next: (result) => {
          // this.getPageCountSuccess(res, prefs, file)
          this.addFile(result, localFile);
          const size = Number(localFile.size * 0.000001);
          this.totalSize += size;
          this.totalSizeToPreview = this.totalSize.toFixed(2);
          resolve(1);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  getPageCountSuccess(result, item, fileData) {
    item.totalPages = result;
    item.secondInput = result;
    item.name = fileData.name;
    item.lastModified = fileData.lastModified;
    this.dataArrayUploadFile.push(item);
    this.uploadedFiles.push(fileData);
    const size = Number(fileData * 0.000001);
    this.totalSize += size;
    this.totalSizeToPreview = this.totalSize.toFixed(2);
    this.calculateSize();
  }

  validateFile(file): { isValid: boolean; error: string | undefined } {
    if (file.size === 0) {
      return {
        isValid: false,
        error: this.translateService.instant('FILE_IS_EMPTY'),
      };
    }

    if (!this.isFileSupportedWithType(file.name, file.type)) {
      return {
        isValid: false,
        error: this.translateService.instant('FILE_TYPE_NOT_SUPPORTED'),
      };
    }

    if (this.totalSize > this.maxFileSize()) {
      return {
        isValid: false,
        error:
          this.translateService.instant('FILE_IS_TOO_LARGE_MAX_FILE_SIZE') +
          ': ' +
          this.maxFileSize(),
      };
    }
    const size = Number(file.size * 0.000001);
    if (size > this.maxFileSize()) {
      return {
        isValid: false,
        error:
          this.translateService.instant('FILE_IS_TOO_LARGE_MAX_FILE_SIZE') +
          ': ' +
          this.maxFileSize(),
      };
    }
    if (size + this.totalSize > this.maxFileSize()) {
      return {
        isValid: false,
        error: this.translateService.instant('FILES_SIZE_TOO_LARGE_SKIPPING_FILE'),
      };
    }

    return {
      isValid: true,
      error: undefined,
    };
  }

  // Remote providers expose name/type/size before download. Reuse the existing
  // validation rules on that lightweight metadata so oversized files are rejected
  // before any cloud download allocates the file body in memory.
  validateRemoteDescriptor(file: {
    name: string;
    mimeType: string;
    size: number;
  }): { isValid: boolean; error: string | undefined } {
    return this.validateFile({
      name: file.name,
      type: file.mimeType,
      size: Number(file.size || 0),
    });
  }

  maxFileSize() {
    return this.getCustomerDetails?.customizationsText?.SelectFileSection?.MaxFileSize ?? 75;
  }

  getExtension(name: string): string {
    return name.substring(name.lastIndexOf('.') + 1).toLowerCase();
  }

  getExtensionUsingType(type: string): string {
    return type.substring(type.lastIndexOf('/') + 1).toLowerCase();
  }

  isFileSupported(name: string): boolean {
    try {
      const ext = this.getExtension(name);
      return this.supportedFiles().indexOf(ext) !== -1;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  isFileSupportedWithType(name: string, type: string): boolean {
    try {
      const isExtenstionAvail = name.indexOf('.');
      let ext = this.getExtension(name);
      if (isExtenstionAvail === -1) {
        ext = this.getExtensionUsingType(type);
      }
      return this.supportedFiles().indexOf(ext) !== -1;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  blackAndWhitePageCost() {
    return (
      this.getCustomerDetails?.customizationsText?.SelectFileSection?.CostPerPage?.Grayscale?.toString() ??
      '-'
    );
  }
  colorPageCost() {
    return (
      this.getCustomerDetails?.customizationsText?.SelectFileSection?.CostPerPage?.ColorCost?.toString() ??
      '-'
    );
  }

  supportedFiles() {
    return (
      this.getCustomerDetails?.customizationsText?.SelectFileSection?.SupportedFileTypes ?? [
        'pdf',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'ppt',
        'pptx',
        'txt',
        'html',
        'rtf',
        'jpg',
        'gif',
        'png',
        'tif',
        'bmp',
        'jpeg',
        'vnd.openxmlformats-officedocument.wordprocessingml.document',
        'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ]
    );
  }

  supportedFilesToPreview() {
    return JSON.stringify(this.supportedFiles())
      ?.replace('[', '')
      ?.replace(']', '')
      ?.replace(/,/g, ', ')
      ?.replace(/"/g, '')
      ?.replace(/, /g, ', .');
  }

  getMimeTypes() {
    const supportedFiles = this.supportedFiles();
    return supportedFiles.map(
      (fileType) => EXTENSION_TO_MIME[fileType] || 'application/octet-stream'
    );
  }

  getFileMimeTypes() {
    return Object.values(EXTENSION_TO_MIME)
      .map((d) => d)
      .join(',');
  }

  calculateSize() {
    this.totalSize = 0;
    for (const file of this.dataArrayUploadFile) {
      const size = file.size;
      this.totalSize += size;
      this.totalSizeToPreview = this.totalSize.toFixed(2);
    }
  }

  reset() {
    this.totalSize = 0;
    this.totalSizeToPreview = '0.0';
    this.uploadedFiles = [];
    this.dataArrayUploadFile = [];
  }

  base64ArrayBuffer(arrayBuffer): string {
    let base64 = '';
    const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

    const bytes = new Uint8Array(arrayBuffer);
    const byteLength = bytes.byteLength;
    const byteRemainder = byteLength % 3;
    const mainLength = byteLength - byteRemainder;

    let a;
    let b;
    let c;
    let d;
    let chunk;

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i = i + 3) {
      // Combine the three bytes into a single integer
      chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

      // Use bitmasks to extract 6-bit segments from the triplet
      a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
      b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
      c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
      d = chunk & 63; // 63       = 2^6 - 1

      // Convert the raw binary segments to the appropriate ASCII encoding
      base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder === 1) {
      chunk = bytes[mainLength];

      a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

      // Set the 4 least significant bits to zero
      b = (chunk & 3) << 4; // 3   = 2^2 - 1

      base64 += encodings[a] + encodings[b] + '==';
    } else if (byteRemainder === 2) {
      chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

      a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
      b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

      // Set the 2 least significant bits to zero
      c = (chunk & 15) << 2; // 15    = 2^4 - 1

      base64 += encodings[a] + encodings[b] + encodings[c] + '=';
    }

    return base64;
  }

  // Google Drive now downloads through fetch() instead of the native HTTP plugin.
  // The current queue and page-count flow still expects base64, so convert the
  // browser Blob here in one shared place instead of duplicating FileReader logic.
  blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] || '');
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }

  removeFile(file, i: number) {
    this.uploadedFiles.splice(i, 1);
    this.dataArrayUploadFile.splice(i, 1);
    this.calculateSize();
  }
  getHoursDiff(startDate, endDate) {
    const msInHour = 1000 * 60 * 60;

    return Math.round(Math.abs(endDate - startDate) / msInHour);
  }
  private addFile(pageCount, file: LocalFile) {
    const prefs = {
      copies: 1,
      name: file.fileName,
      fileName: file.fileName,
      extension: file.fileName.substring(file.fileName.lastIndexOf('.') + 1).toLowerCase(),
      contentType: file.contentType,
      lastModified: file.lastModified,
      printType: this.getCustomerDetails?.jobLists[0]?.DefaultValues.Color,
      duplex: this.getCustomerDetails?.jobLists[0]?.DefaultValues.Duplex,
      paperSize: this.getCustomerDetails?.jobLists[0]?.DefaultValues.PaperSize,
      layout: this.getCustomerDetails?.jobLists[0]?.DefaultValues.Orientation,
      pageRange: this.getCustomerDetails?.jobLists[0]?.PageRange.Enabled
        ? this.getCustomerDetails?.jobLists[0]?.PageRange.Default
        : 'all',
      firstInput: 1,
      secondInput: pageCount == 0 ? 1 : pageCount,
      totalPages: pageCount == 0 ? 1 : pageCount,
      isPagesTrue: false,
      // size: Number((file.size* 0.000001).toFixed(2)),
      size: Number((file.size * 0.000001).toFixed(2)),
      pageRangeEnabled: this.getCustomerDetails?.jobLists[0]?.PageRange?.Enabled,
      pageRangeLimit: this.getCustomerDetails?.jobLists[0]?.PageRange?.Limit,
      isMaxPageRangeExceeded: false,
      pageType: 'all'
    };
    this.dataArrayUploadFile.push(prefs);
    this.uploadedFiles.push(file);
  }

  public timeAgo(dateParam) {
    if (!dateParam) {
      return null;
    }

    const date = typeof dateParam === 'object' ? dateParam : new Date(dateParam);
    const DAY_IN_MS = 86400000; // 24 * 60 * 60 * 1000
    const today = new Date();
    const yesterday = new Date(today.getTime() - DAY_IN_MS);
    const seconds = Math.round((today.getTime() - date) / 1000);
    const minutes = Math.round(seconds / 60);
    const isToday = today.toDateString() === date.toDateString();
    const isYesterday = yesterday.toDateString() === date.toDateString();
    const isThisYear = today.getFullYear() === date.getFullYear();

    if (seconds < 50) {
      return this.translateService.instant('NOW');
    }
    // else if (seconds < 60) {
    //   return `${ seconds } seconds ago`;
    // }
    else if (seconds < 90) {
      return this.translateService.instant('A_MINUTE_AGO');
    } else if (minutes < 60) {
      return `${minutes} ${this.translateService.instant('MINUTE_AGO')}`;
    } else if (isToday) {
      return this.getFormattedDate(date, this.translateService.instant('TODAY')); // Today at 10:20
    } else if (isYesterday) {
      return this.getFormattedDate(date, this.translateService.instant('YESTERDAY')); // Yesterday at 10:20
    } else if (isThisYear) {
      return this.getFormattedDate(date, false, true); // 10. January at 10:20
    }

    return this.getFormattedDate(date); // 10. January 2017. at 10:20
  }
  public getFormattedDate(date, prefomattedDate: any = false, hideYear: any = false) {
    const day = date.getDate();
    const month = new DiffConstants().MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    let minutes = date.getMinutes();

    if (minutes < 10) {
      // Adding leading zero to minutes
      minutes = `0${minutes}`;
    }

    if (prefomattedDate) {
      // Today at 10:20
      // Yesterday at 10:20
      return `${prefomattedDate} ${this.translateService.instant('TIME_AT')} ${hours}:${minutes}`;
    }

    if (hideYear) {
      // 10. January at 10:20
      return `${day}. ${month} ${this.translateService.instant('TIME_AT')} ${hours}:${minutes}`;
    }

    // 10. January 2017. at 10:20
    return `${day}. ${month} ${year}. ${this.translateService.instant(
      'TIME_AT'
    )} ${hours}:${minutes}`;
  }

  async showAskForPrint(title: string, success: string, cancel: string) {
    const modal = await this.modalCtrl.create({
      component: TwoOptionsPopupComponent,
      componentProps: { title, success, cancel },
      backdropDismiss: true,
      cssClass: 'two-options-popup',
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    return result?.data;
  }
  removeIntractionInProgressMsal() {
    if (this.platform.is('android')) {
      this.authService.instance.handleRedirectPromise();
    }
    sessionStorage.removeItem('msal.interaction.status');
  }
  containURL(urlString: string) {
    return new RegExp(
      '([a-zA-Z0-9]+://)?([a-zA-Z0-9_]+:[a-zA-Z0-9_]+@)?([a-zA-Z0-9.-]+\\.[A-Za-z]{2,4})(:[0-9]+)?(/.*)?'
    ).test(urlString);
  }

  formatFileSize(bytes, decimalPoint) {
    if (bytes == 0) return '0 Bytes';
    var k = 1000,
      dm = decimalPoint || 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  async setCurrencySymbol() {
    const supportedLanguage = this.getCustomerDetails?.supportedLanguages;
    const globalCurrency = this.getCustomerDetails?.customizationsText?.Currency;
    if (globalCurrency && supportedLanguage) {
      const language = supportedLanguage.find((data) => data.currency === globalCurrency);
      if (language) {
        this.currencySymbol = language?.currencySymbol || language?.symbol || '$';
      }
    }
  }

  isInvalidFile(file: any): boolean {
    return (
      file.isPagesTrue &&
      (file.firstInput > file.secondInput ||
        file.firstInput > file.totalPages ||
        file.secondInput > file.totalPages)
    );
  }

  checkPageRangeLimit(file: any): boolean {
    return file.pageRangeEnabled && file.totalPages > file.pageRangeLimit;
  }

  validatePageRange(file:any): boolean {
    return file.pageRangeEnabled && ((file.secondInput - file.firstInput) + 1 > file.pageRangeLimit);
  }

  calculateBaseCost(file: any): number {
    const color = file.printType.toLowerCase();
    const copies = Number(file.copies) || 1; // default to 1 if copies is not valid
    let value = 0;
    const costPerPage =
      this.getCustomerDetails?.customizationsText?.SelectFileSection?.CostPerPage ||
      ({} as CostPerPage);
    value = color === 'color' ? costPerPage.ColorCost : costPerPage.Grayscale;
    let pages = Number(file.totalPages);
    if (file.isPagesTrue) {
      pages = Number(file.secondInput) - Number(file.firstInput) + 1;
    }
    return Number(value || 0) * copies * pages;
  }

  calculatePaperSizeCost(file: any, baseCost: number, paperSizeList: any[]): number {
    const paperSize = file.paperSize;
    const duplex = file.duplex;

    if (!!this.getCustomerDetails?.customizationsText?.SelectFileSection
      ?.CostPerPageVisibility && !this.getCustomerDetails?.jobLists?.[0]?.ChargeForUsage ) {
      return baseCost;
    }
    const paperSizePrice = paperSizeList.find((item) => item.PaperSizeName === paperSize);
    if (!paperSizePrice?.Price) {
      return baseCost;
    }

    const color = file.printType.toLowerCase();
    const singleSideRate = paperSizePrice.Price[color === 'color' ? 'Color' : 'Grayscale'] || 0;
    const doubleSideRate =
      paperSizePrice.Price[color === 'color' ? 'ColorDuplex' : 'GrayscaleDuplex'] || 0;

    const totalPages = file.totalPages;
    let baseRate = duplex ? doubleSideRate : singleSideRate;
    let value = baseRate * (file.copies || 1) * totalPages;

    if (duplex && totalPages > 1 && totalPages % 2 !== 0) {
      value = baseRate * (file.copies || 1) * (totalPages - 1) + singleSideRate;
    }

    return value;
  }

  roundToTwoDecimalPlaces(value: number): number {
    return parseFloat(value.toFixed(2));
  }

  calculateTotalFilesSize(): number {
    let totalFilesSize = 0;
    this.uploadedFiles.forEach((file) => {
      totalFilesSize += file.size;
    });
    return totalFilesSize * 0.000001; // Convert bytes to MB
  }
}
